import { ProjectsService } from '@core/application/projects.service'
import { ReviewsService } from '@core/application/reviews.service'
import { Project } from '@core/domain/models/project.model'
import { Id } from '@core/domain/types/id.type'
import { Ref, ref } from 'vue'
import { useSettings } from '../../composables/use-settings'
import useDisplaySettings from './use-display-settings'
import {
  EvidenceServiceApiKey,
  ProjectsServiceKey,
  ReviewsServiceKey,
} from '@app/injectionKeys'
import { injectStrict } from '@app/utils/injectStrict'
import { User } from '@core/domain/models/user.model'

const settings = useSettings()
const displaySettings = useDisplaySettings()

const projects: Ref<Project[]> = ref([])
export default () => {
  const projectsService: ProjectsService = injectStrict(ProjectsServiceKey)
  const reviewsService: ReviewsService = injectStrict(ReviewsServiceKey)
  const evidenceSdk = injectStrict(EvidenceServiceApiKey)

  const create = async (data: { name: string; teamId: string }) => {
    const project = await projectsService.create(data)
    projects.value.unshift(project)
    return project
  }

  const refresh = async () => {
    const fetchedProjects = await projectsService.find()
    projects.value = fetchedProjects.map((p) => {
      settings.favoriteProjects.value?.forEach((fav) => {
        if (p.id === fav) p.isFavorite = true
      })
      return p
    })
  }

  function toggleFavorite(projectId: Id) {
    const projectIndex = projects.value.findIndex((p) => p.id === projectId)
    if (!projects.value[projectIndex].isFavorite) {
      projects.value[projectIndex].isFavorite = true
      settings.addProjectTofavorites(projectId)
    } else {
      projects.value[projectIndex].isFavorite = false
      settings.removeProjectFromfavorites(projectId)
    }
  }

  async function deleteProject(projectId: Id) {
    const project = projects.value.find((p) => p.id === projectId)
    if (!project) throw new Error('project not found')
    await projectsService.delete(projectId)
    settings.removeProjectFromfavorites(projectId)
    projects.value = projects.value.filter((p) => p.id !== projectId)
  }

  async function updateName(projectId: Id, name: string) {
    await projectsService.updateProjectName(projectId, name)
    const index = projects.value.findIndex((p) => p.id === projectId)
    projects.value[index] = { ...projects.value[index], name }
  }

  async function updateContactForComplaintEmail(
    projectId: Id,
    contactForComplaintEmail: string,
  ) {
    await projectsService.updateContactForComplaintEmail(
      projectId,
      contactForComplaintEmail,
    )
    const index = projects.value.findIndex((p) => p.id === projectId)
    projects.value[index] = {
      ...projects.value[index],
      contactForComplaintEmail,
    }
  }

  async function updateReviewSeriesName(
    _projectId: Id,
    reviewSeriesId: string,
    name: string,
  ) {
    await evidenceSdk.api.reviewSeriesControllerUpdateName(reviewSeriesId, {
      name,
    })
    await refresh()
  }

  async function rollbackReviewSeries(projectId: Id, reviewSeriesId: string) {
    const project = projects.value.find((p) => p.id === projectId)
    if (!project) throw new Error('project not found')
    await reviewsService.rollbackReviewSeries(reviewSeriesId)
    refresh()
  }

  async function deleteReviewSeries(projectId: Id, reviewSeriesId: string) {
    const project = projects.value.find((p) => p.id === projectId)
    if (!project) throw new Error('project not found')
    await evidenceSdk.api.reviewSeriesControllerDelete(reviewSeriesId)
    refresh()
  }

  async function updateCslStyle(projectId: Id, cslStyle: string) {
    await projectsService.updateCslStyle(projectId, cslStyle)
    const index = projects.value.findIndex((p) => p.id === projectId)
    projects.value[index].cslStyle = cslStyle
  }

  async function addProjectMember(projectId: Id, user: User) {
    await projectsService.addMember(projectId, user.id)
    const projectIndex = projects.value.findIndex((p) => p.id === projectId)
    if (projectIndex === -1) throw new Error('project not found')
    const project = { ...projects.value[projectIndex] }
    project.users.push(user)
    projects.value[projectIndex] = { ...project }
  }

  async function removeProjectMember(projectId: Id, userId: string) {
    await projectsService.removeMember(projectId, userId)
    const projectIndex = projects.value.findIndex((p) => p.id === projectId)
    if (projectIndex === -1) throw new Error('project not found')
    const project = { ...projects.value[projectIndex] }
    project.users = project.users.filter((u) => u.id !== userId)
    projects.value[projectIndex] = { ...project }
  }

  async function moveReviewSeries(reviewSeriesId: string, order: string) {
    await evidenceSdk.api.reviewSeriesControllerUpdateOrder(reviewSeriesId, {
      order,
    })
    await refresh()
  }

  async function updateReviewVersionName({
    name,
    reviewId,
  }: {
    reviewId: number
    name: string
  }) {
    await evidenceSdk.api.reviewsControllerUpdateReviewVersionName(reviewId, {
      name: name,
    })

    await refresh()
  }

  return {
    projects,
    displaySettings,
    create,
    refresh,
    deleteProject,
    updateName,
    updateReviewSeriesName,
    rollbackReviewSeries,
    updateCslStyle,
    updateSupevisorEmail: updateContactForComplaintEmail,
    toggleFavorite,
    addProjectMember,
    removeProjectMember,
    moveReviewSeries,
    deleteReviewSeries,
    updateReviewVersionName,
  }
}
